import React from 'react';

import { Platform } from 'react-native';

import { Helmet } from 'components/helmet';
import { IImage } from 'generated/sanity-graphql';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { fullBrandName } from 'utils/environment';

interface IStoreMeta {
  phoneNumber: string;
  city: string;
  state: string;
  postalCode: string;
  streetAddress: string;
  atCopy: string;
  image?: IImage;
  services: string[];
  canonicalUrl?: string;
}

interface ILdJsonRestaurant {
  '@context': 'http://schema.org';
  '@type': 'Restaurant';
  address: {
    '@type': 'PostalAddress';
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    streetAddress: string;
  };
  name: string;
  telephone: string;
  url?: string;
  image?: string;
}

const StoreMetaForSEO: React.FC<React.PropsWithChildren<IStoreMeta>> = ({
  phoneNumber,
  city,
  state,
  postalCode,
  streetAddress,
  atCopy,
  image,
  services,
  canonicalUrl,
}) => {
  const { locale } = useLocale();

  if (Platform.OS !== 'web') {
    return null;
  }

  const location = `${streetAddress}, ${city}, ${state}`;

  // Ensure proper translation
  const translatedDescription = services.length
    ? t('storeLocationMetaDescriptionWithServices', {
        location,
        services: services.slice(0, 3).join(', '),
        defaultValue:
          'Visit Burger King at {location}. View restaurant hours and available services, including {services}.',
      })
    : t('storeLocationMetaDescription', {
        location,
        defaultValue:
          'Visit Burger King at {location}. View restaurant hours and available services.',
      });

  const storeSEOMeta = {
    title: location,
    description: translatedDescription,
    ogUrl: canonicalUrl,
    ogLocale: locale.replace('-', '_'),
  };

  const ldJsonRestaurantData: ILdJsonRestaurant = {
    '@context': 'http://schema.org',
    '@type': 'Restaurant',
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: state,
      postalCode,
      streetAddress,
    },
    name: `${fullBrandName()} ${atCopy} ${streetAddress}`,
    telephone: phoneNumber,
    ...(canonicalUrl && { url: canonicalUrl }), // If canonical is empty then omit url field
  };

  if (image) {
    ldJsonRestaurantData.image = image?.asset?.url || undefined;
  }

  return (
    <Helmet>
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta name="description" content={storeSEOMeta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Burger King" />
      <meta property="og:title" content={`${storeSEOMeta.title} - Burger King`} />
      <meta property="og:description" content={storeSEOMeta.description} />
      {storeSEOMeta.ogUrl && <meta property="og:url" content={storeSEOMeta.ogUrl} />}
      <meta property="og:locale" content={storeSEOMeta.ogLocale} />
      <meta name="twitter:site" content="@BurgerKing" />
      <meta name="twitter:title" content={`${storeSEOMeta.title} - Burger King`} />
      <meta name="twitter:description" content={storeSEOMeta.description} />
      <title>{storeSEOMeta.title}</title>
      <script type="application/ld+json">{JSON.stringify(ldJsonRestaurantData)}</script>
    </Helmet>
  );
};

export default StoreMetaForSEO;
